import React, { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    getToken,
    user,
    getPermission,
    logout,
    getClaim,
  } = useKindeAuth();

  const navigate = useNavigate();
  const maxRetries = 10;
  const retryDelay = 1200;

  const initialAuthContext = {
    user: {
      userId: "",
      authId: "",
      accessLevel: 1,
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      imageUrl: "",
      roles: [],
      themePreference: null,
    },
    organization: {
      authOrgId: "",
      orgId: "",
      orgName: "",
      stripeCustomerId: "",
      initializationComplete: "",
      planId: "",
      planStatus: "",
      planName: "",
      planTrialEnd: "",
      planTrialEndDate: "",
      planFeatures: {},
    },
    onboarding: {
      needsUsers: true,
      needsCustomers: true,
      needsSites: true,
      isOnboarding: false,
    },
  };

  const [authContext, setAuthContext] = useState(initialAuthContext);
  const [authContextLoading, setAuthContextLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [provisioningFailed, setProvisioningFailed] = useState(false);

  const resetAuthContext = () => {
    setAuthContext(initialAuthContext);
  };

  const processUserData = useCallback(
    (data) => {
      const rolesClaim = getClaim("roles");
      const roles = rolesClaim?.value || [];

      const newAuthContext = {
        user: {
          userId: data.id,
          authId: data.auth_id || data.id,
          accessLevel: "1",
          firstName: data.first_name,
          lastName: data.last_name,
          email: data.email,
          phoneNumber: data.phone_number,
          imageUrl: data.profile_url,
          roles,
          themePreference: data.theme_preference || "system",
        },
        organization: {
          authOrgId: getPermission()?.orgCode || "",
          orgId: data.org_id,
          orgName: data.org_name,
          stripeCustomerId: data.stripe_customer_id,
          initializationComplete: data.initialization_complete,

          planId: data.subscription?.id || "",
          planStatus: data.subscription?.status || "",
          planName: data.subscription?.name || "",
          planTrialEnd: data.subscription?.trialEnd || "",
          planTrialEndDate: data.subscription?.trialEndDate || "",
          planFeatures: data.subscription?.features || {},
        },
        onboarding: {
          needsUsers: data.onboarding?.needsUsers ?? true,
          needsCustomers: data.onboarding?.needsCustomers ?? true,
          needsSites: data.onboarding?.needsSites ?? true,
          isOnboarding: data.onboarding?.isOnboarding ?? false,
        },
      };

      setAuthContext(newAuthContext);
    },
    [getPermission, getClaim]
  );

  const fetchUserData = useCallback(async () => {
    if (!isAuthenticated || isLoading || !user) {
      setAuthContextLoading(false);
      return false;
    }

    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/v1/auth/login/${user.id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();


      if (data.provisioning) {
        return false; // signal to retry
      }

      processUserData(data);
      setProvisioningFailed(false);
      setRetryCount(0);
      setAuthContextLoading(false);
      return true; // success
    } catch (err) {
      console.error("Error fetching user data:", err);
      setAuthContextLoading(false);
      return false;
    }
  }, [isAuthenticated, isLoading, user, retryCount, getToken, processUserData]);

  const handleLogout = useCallback(async () => {
    try {
      // Don't reset context here - keep navigation visible during redirect
      await logout({
        logoutRedirectURL: `${window.location.origin}/auth/logout`
      });
      // resetAuthContext will be called when the logout page loads
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }, [logout]);

  // Initial data fetch
  useEffect(() => {
    if (isAuthenticated && !isLoading && retryCount === 0) {
      fetchUserData().then((success) => {
        if (!success) {
          setRetryCount(1); // ✅ kickstart retry loop
        }
      });
    }

    if (!isAuthenticated && !isLoading) {
      setAuthContextLoading(false);
    }
  }, [isAuthenticated, isLoading, retryCount, fetchUserData]);

  // Retry loop
  useEffect(() => {
    if (retryCount > 0 && retryCount < maxRetries) {
      const timeout = setTimeout(() => {
        fetchUserData().then((success) => {
          if (!success) {
            setRetryCount((prev) => prev + 1);
          }
        });
      }, retryDelay);

      return () => clearTimeout(timeout);
    }

    if (retryCount >= maxRetries) {
      console.error("Max retries reached. User still not provisioned.");
      setProvisioningFailed(true); // ✅ mark failed
      setAuthContextLoading(false);
    }
  }, [retryCount, fetchUserData]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        getToken,
        authContext,
        authContextLoading,
        retryCount,
        provisioningFailed,
        setAuthContext,
        resetAuthContext,
        logout: handleLogout,
        fetchUserData,
        onboarding: authContext.onboarding,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
